'use client';

import React, { useState, useEffect } from 'react';
import Topbar from '@/components/Topbar';
import Footer from '@/components/Footer';
import { createClient } from '@/lib/supabase/client';
import { MapPin, Calendar, PawPrint, Loader2, ShieldCheck } from 'lucide-react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';

type BreederListing = {
  id: string;
  litter_title: string;
  breed: string;
  breeder_name: string;
  location: string;
  date_of_birth: string | null;
  ready_to_leave_date: string | null;
  males_available: number;
  females_available: number;
  price: number | null;
  images: { src: string; alt: string }[];
  seller_id: string;
  seller?: { full_name: string; is_verified: boolean };
};

export default function BreedersPage() {
  const [listings, setListings] = useState<BreederListing[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    const supabase = createClient();
    supabase
      .from('breeder_listings')
      .select('id, litter_title, breed, breeder_name, location, date_of_birth, ready_to_leave_date, males_available, females_available, price, images, seller_id, seller:user_profiles(full_name, is_verified)')
      .eq('listing_status', 'active')
      .eq('moderation_status', 'approved')
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        setListings((data as unknown as BreederListing[]) || []);
        setLoading(false);
      });
  }, []);

  const filtered = listings.filter((l) =>
    !search.trim() ||
    l.litter_title.toLowerCase().includes(search.toLowerCase()) ||
    l.breed.toLowerCase().includes(search.toLowerCase()) ||
    l.location?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[hsl(222,47%,6%)]">
      <Topbar />
      <main className="pt-16">
        <div className="max-w-screen-2xl mx-auto px-4 lg:px-8 xl:px-10 py-10">
          {/* Header */}
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="inline-flex items-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-full px-3 py-1 mb-3">
                <PawPrint className="w-3.5 h-3.5 text-amber-400" />
                <span className="text-amber-400 text-xs font-semibold">Responsible Breeders Only</span>
              </div>
              <h1 className="text-2xl font-bold text-white mb-1">Breeder Listings</h1>
              <p className="text-slate-500 text-sm">Litters, planned breedings, and available dogs from verified breeders</p>
            </div>
            <Link href="/breeders/create" className="hidden sm:flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold px-4 py-2 rounded-lg text-sm transition-all duration-150">
              <PawPrint className="w-4 h-4" />
              Post Breeder Listing
            </Link>
          </div>

          {/* Compliance notice */}
          <div className="bg-amber-500/5 border border-amber-500/20 rounded-xl p-4 mb-8">
            <div className="flex items-start gap-3">
              <ShieldCheck className="w-4 h-4 text-amber-400 mt-0.5 shrink-0" />
              <div className="text-xs text-slate-400 leading-relaxed">
                <span className="text-amber-400 font-semibold">UK Compliance Notice: </span>
                All breeder listings are reviewed before going live. In England, breeding businesses may require a licence from their local authority. Buyers should purchase puppies directly from breeders. All dogs must be microchipped by 8 weeks old. DrivenMarket does not facilitate third-party puppy sales.
              </div>
            </div>
          </div>

          {/* Search */}
          <div className="mb-6">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by breed, location, or litter name..."
              className="w-full max-w-md bg-[hsl(222,35%,12%)] border border-[hsl(222,25%,18%)] text-slate-100 placeholder-slate-500 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-amber-500/60 focus:ring-1 focus:ring-amber-500/30 transition-all duration-150"
            />
          </div>

          {/* Listings */}
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 text-amber-400 animate-spin" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-20 text-center border border-dashed border-[hsl(222,25%,18%)] rounded-2xl">
              <div className="w-14 h-14 rounded-2xl bg-[hsl(222,35%,12%)] border border-[hsl(222,25%,18%)] flex items-center justify-center mb-4">
                <PawPrint className="w-6 h-6 text-slate-500" />
              </div>
              <h3 className="text-base font-semibold text-white mb-2">
                {search ? 'No matching breeder listings' : 'No breeder listings yet'}
              </h3>
              <p className="text-sm text-slate-500 mb-5 max-w-xs">
                {search ? 'Try a different search term.' : 'Be the first to post a responsible breeder listing on DrivenMarket.'}
              </p>
              {!search && (
                <Link href="/breeders/create" className="flex items-center gap-2 bg-amber-500 hover:bg-amber-400 text-slate-900 font-semibold px-4 py-2.5 rounded-lg text-sm transition-all duration-150">
                  Post Breeder Listing
                </Link>
              )}
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
              {filtered.map((listing) => {
                const firstImage = listing.images?.[0];
                const readyDate = listing.ready_to_leave_date
                  ? new Date(listing.ready_to_leave_date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
                  : null;
                const totalAvailable = (listing.males_available || 0) + (listing.females_available || 0);

                return (
                  <Link key={listing.id} href={`/breeders/${listing.id}`} className="listing-card group">
                    <div className="relative aspect-[4/3] overflow-hidden bg-[hsl(222,35%,10%)]">
                      {firstImage ? (
                        <AppImage src={firstImage.src} alt={firstImage.alt || listing.litter_title} fill sizes="(max-width: 640px) 100vw, 25vw" className="object-cover group-hover:scale-105 transition-transform duration-300" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <PawPrint className="w-8 h-8 text-slate-600" />
                        </div>
                      )}
                      <div className="absolute top-2 left-2 bg-amber-500/90 text-slate-900 text-xs font-bold px-2 py-0.5 rounded-md">
                        {listing.breed}
                      </div>
                    </div>
                    <div className="p-3.5">
                      <h3 className="text-sm font-semibold text-white leading-snug line-clamp-2 group-hover:text-amber-400 transition-colors duration-150 mb-1.5">
                        {listing.litter_title}
                      </h3>
                      <p className="text-xs text-slate-500 mb-2">{listing.breeder_name}</p>
                      {listing.price && (
                        <div className="text-base font-bold text-white tabular-nums mb-2">
                          £{listing.price.toLocaleString()} per puppy
                        </div>
                      )}
                      <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap">
                        {listing.location && (
                          <div className="flex items-center gap-1"><MapPin className="w-3 h-3" />{listing.location}</div>
                        )}
                        {readyDate && (
                          <div className="flex items-center gap-1"><Calendar className="w-3 h-3" />Ready {readyDate}</div>
                        )}
                      </div>
                      {totalAvailable > 0 && (
                        <div className="mt-2 pt-2 border-t border-[hsl(222,25%,18%)] flex items-center gap-2 text-xs">
                          {listing.males_available > 0 && <span className="text-blue-400">{listing.males_available}M</span>}
                          {listing.females_available > 0 && <span className="text-pink-400">{listing.females_available}F</span>}
                          <span className="text-slate-500">available</span>
                        </div>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}
